// ── THE ELECTION, WITHOUT THE FARM'S BASE ACRES OR PAYMENT YIELD ─────────────
//
// Kevin, 2026-09-17: *"ARC/PLC yields have nothing to do with what a farmer's
// actual yields are. We need to be able to build this without having their base
// acres or base yields."*
//
// Both halves are right, and the first one is ALGEBRA rather than a convenience.
// Both programs pay on the SAME 85% of the SAME base acres under the SAME share:
//
//     PLC     = PLC_rate x payment_yield x 0.85 x base_acres x share
//     ARC-CO  = ARC_rate                 x 0.85 x base_acres x share
//
// so every one of those terms appears on both sides and divides out:
//
//     PLC wins  <=>  payment_yield > ARC_rate / PLC_rate
//
// Base acres, the 85% factor and the producer's share are MAGNITUDE. They answer
// "how many dollars", never "which program". The only farm quantity left is the
// FSA payment yield, and it enters as a THRESHOLD TO CLEAR — so this module never
// needs to hold it either. It states the breakeven; the grower reads their own
// payment yield off their FSA record and compares.
//
// ⚠ AND THAT PAYMENT YIELD IS NOT ANY YIELD THIS APP HOLDS. It is a separate FSA
// determination, historically rooted. It is not the APH, not the actual harvested
// yield, and not the county benchmark. Nothing here estimates it, defaults it or
// stores it — those were three different ways of getting it wrong, and the
// Coverage Advisor's own "default payment yield of 60 bu" is why the distinction
// is written at the top of the file rather than buried.
//
// This module EXTENDS src/strategy/arcPlcRule.js and does not restate any of it.
// The cap, the 85% factor and the two rate functions are imported, so there is
// still exactly one copy of the money arithmetic.
import { ARC_CAP_FRAC, BASE_ACRE_FACTOR, arcRatePerAcre, plcRateFrom, arcCapRate, programPrice } from "./arcPlcRule";

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));

// ── PRACTICE: FSA BLENDS, IT DOES NOT CHOOSE ─────────────────────────────────
//
// 7 CFR 1412, "Benchmark revenue for ARC-CO": *"If a county has separate
// irrigated and non-irrigated yields established for a covered commodity, the
// benchmark revenue calculated by FSA for that farm and covered commodity will be
// weighted based on the farm's historical irrigated percentage."* §1412.53(c)
// says that percentage comes from the farm's own 2019-2023 reported acreage and
// is applied against the benchmark revenue, the guarantee AND the actual revenue
// — which is why the YIELD is blended here too, not only the dollars.
//
// So an all-dryland farm is 0 and an all-irrigated farm is 100 — the tick box an
// agent actually wants — and a genuinely mixed farm gets its real percentage
// rather than being rounded onto a side.
//
// ⚠ SPLIT COUNTIES CARRY NO "ALL" ROW. Measured 2026-09-17 over FSA's own files:
// 176 CY2026 county/crop cells across the six states this book farms are split,
// none of them has an ALL row, and 13 are counties with customers in them. The
// Coverage Advisor used to keep whichever row arrived first, which is how Van
// Buren corn could read a $1,068.62 benchmark or a $806.71 one depending on
// nothing at all.
export const PRACTICE_ALL = "ALL";
export const PRACTICE_IRR = "IRRIGATED";
export const PRACTICE_NON = "NONIRRIGATED";

// Returns the county figures to rate against, or a REFUSAL naming what is missing.
// `rows` is keyed by practice. `irrigatedPct` is 0-100, or null when unanswered.
export function resolvePracticeRow(rows, irrigatedPct) {
  const r = rows || {};
  const all = r[PRACTICE_ALL] || null;
  const irr = r[PRACTICE_IRR] || null;
  const non = r[PRACTICE_NON] || null;
  if (all) return { row: all, split: false, blended: false, practice: PRACTICE_ALL };
  // Only one side published — nothing to blend, and no question to ask.
  if (irr && !non) return { row: irr, split: false, blended: false, practice: PRACTICE_IRR };
  if (non && !irr) return { row: non, split: false, blended: false, practice: PRACTICE_NON };
  if (!irr && !non) return { row: null, split: false, blended: false, practice: null,
                             refused: "no county row for this crop and year" };
  const pct = num(irrigatedPct);
  // ⚠ REFUSE RATHER THAN PICK. Without the percentage there is no single ARC-CO
  // figure for this farm, and silently taking one side is the defect this exists
  // to end. The caller shows BOTH and says why.
  if (pct == null || pct < 0 || pct > 100)
    return { row: null, split: true, blended: false, practice: null,
             irrigated: irr, nonIrrigated: non,
             refused: "this county is split by irrigation practice and the farm's irrigated percentage is not known" };
  const f = pct / 100, g = 1 - f;
  const mix = (a, b) => {
    const x = num(a), y = num(b);
    return (x == null || y == null) ? null : f * x + g * y;
  };
  return {
    row: {
      benchmark_yield: mix(irr.benchmark_yield, non.benchmark_yield),
      benchmark_revenue: mix(irr.benchmark_revenue, non.benchmark_revenue),
      guarantee_revenue: mix(irr.guarantee_revenue, non.guarantee_revenue),
      actual_yield: mix(irr.actual_yield, non.actual_yield),
      actual_revenue: mix(irr.actual_revenue, non.actual_revenue),
      payment_rate: mix(irr.payment_rate, non.payment_rate),
    },
    split: true, blended: true, practice: null, irrigatedPct: pct,
    irrigated: irr, nonIrrigated: non,
  };
}

// ── THE BREAKEVEN ────────────────────────────────────────────────────────────
//
// The whole election, in one number, from county data alone.
export function breakevenPaymentYield(arcRate, plcRate) {
  const a = num(arcRate), p = num(plcRate);
  if (a == null || p == null) return null;
  // PLC cannot pay at this price, so no payment yield makes it win. Undefined,
  // not infinite, and not a very large number that would render as a threshold.
  if (p <= 0) return null;
  return a / p;
}

// A full read of the election. `paymentYield` is OPTIONAL — without it the answer
// is the threshold, which is the useful answer and the one that needs no farm data.
export function electionRead({ arcRate, plcRate, paymentYield }) {
  const a = num(arcRate), p = num(plcRate), y = num(paymentYield);
  if (a == null || p == null)
    return { state: "unknown", breakeven: null, pick: null, arcPerAcre: null, plcPerAcre: null };
  const arcPerAcre = a * BASE_ACRE_FACTOR;
  const be = breakevenPaymentYield(a, p);
  if (a <= 0 && p <= 0)
    return { state: "neither_pays", breakeven: null, pick: null, arcPerAcre: 0, plcPerAcre: 0 };
  if (p <= 0)
    return { state: "arc_only", breakeven: null, pick: "ARC-CO", arcPerAcre, plcPerAcre: 0 };
  if (a <= 0)
    return { state: "plc_only", breakeven: 0, pick: "PLC", arcPerAcre: 0,
             plcPerAcre: y != null && y > 0 ? p * y * BASE_ACRE_FACTOR : null };
  if (y == null || y <= 0)
    return { state: "threshold", breakeven: be, pick: null, arcPerAcre, plcPerAcre: null };
  const plcPerAcre = p * y * BASE_ACRE_FACTOR;
  return { state: "decided", breakeven: be, arcPerAcre, plcPerAcre,
           pick: plcPerAcre > arcPerAcre ? "PLC" : "ARC-CO" };
}

// ── THE ONE COUNTY READ — every ARC/PLC surface calls this (2026-09-18) ──────
//
// Kevin's work order E: *"one engine consumed by Coverage Advisor + Full Strategy +
// one-page output … fails if renderers derive their own result."* Before this the
// Coverage Advisor carried its own copy of the ARC-CO arithmetic and decided the
// election at a DEFAULT payment yield (wheat 60 bu, otherwise 72% of the county
// benchmark); the Full Strategy Report priced PLC at a hard-coded 60 bu; the
// election sheet was the only surface on this engine. Three answers to one question.
//
// One county, one reference row, optionally a price and a county-yield scenario,
// optionally the farm's payment yield — and the read that electionRead makes of it.
//
//   price        null -> FSA's own MYA forecast, and FSA's published PLC rate where it
//                         has one. A number -> a scenario at that price; the published
//                         rate belongs to the forecast and is NOT reused.
//   yieldFactor  null -> the county yield FSA has PUBLISHED, else its benchmark (a
//                         normal year). A number -> a scenario at that multiple of the
//                         benchmark. Never a forecast: a yield model is a caller's
//                         scenario, passed as a factor and labelled by the caller.
//   paymentYield OPTIONAL. Without it the answer is the breakeven threshold.
//
// `yieldBasis` says which of the three county yields the ARC-CO figure rests on, so
// a page can say "at a normal county yield" rather than leave the reader to guess.
// What ARC-CO actually PAID for a settled year, per base acre — FSA's own published rate on
// 85% of base acres. A fact, not a read; it lives here so no surface multiplies it itself.
export function settledArcPerAcre(countyRow) {
  const r = num(countyRow && countyRow.payment_rate);
  return r != null ? r * BASE_ACRE_FACTOR : null;
}
export function practiceKeyOf(r) {
  return String((r && r.practice) || PRACTICE_ALL).toUpperCase();
}
export function countyRead({ countyRow, ref, price = null, yieldFactor = null, paymentYield = null } = {}) {
  if (!ref) return { usable: false, reason: "no FSA reference price for this crop and year" };
  if (!countyRow) return { usable: false, reason: "no county figures published for this crop and year" };
  const erp = num(ref.effective_ref_price);
  const atPrice = num(price) != null;
  const mya = atPrice ? num(price) : num(ref.mya_price);
  // FSA's national loan rate for the program year floors the price BOTH programs pay on.
  const loanRate = num(ref.national_loan_rate);
  const pp = programPrice(mya, loanRate);
  const plcRate = plcRateFrom({ plcPaymentRate: atPrice ? null : ref.plc_payment_rate, effectiveRefPrice: erp, myaPrice: mya, loanRate });
  const by = num(countyRow.benchmark_yield), pub = num(countyRow.actual_yield), yf = num(yieldFactor);
  const scenarioYield = yf != null;
  const arcYield = scenarioYield ? (by != null ? by * yf : null) : (pub != null ? pub : by);
  const yieldBasis = scenarioYield ? "scenario" : (pub != null ? "published" : "benchmark");
  // FSA's own actual revenue is used only for FSA's own case — the published yield at
  // the forecast price. Any scenario rebuilds it from yield x price.
  const pubRev = num(countyRow.actual_revenue);
  const actualRevenue = (!atPrice && !scenarioYield && pubRev != null) ? pubRev
    : (arcYield != null && pp != null ? arcYield * pp : null);
  const br = num(countyRow.benchmark_revenue);
  const arcRate = arcRatePerAcre({ guaranteeRevenue: countyRow.guarantee_revenue, actualRevenue, benchmarkRevenue: br });
  const read = electionRead({ arcRate, plcRate, paymentYield });
  return {
    usable: true, price: mya, programPrice: pp, loanRate, referencePrice: erp, plcRate, arcRate,
    arcYield, yieldBasis, actualRevenue,
    arcPerAcre: arcRate != null ? arcRate * BASE_ACRE_FACTOR : null,
    plcPerAcre: read.plcPerAcre, breakeven: read.breakeven, state: read.state, pick: read.pick,
    capped: arcRate != null && br != null && arcRate > 0 && Math.abs(arcRate - arcCapRate(br)) < 0.005,
    paymentYield: num(paymentYield) != null && num(paymentYield) > 0 ? num(paymentYield) : null,
  };
}

// What the read SAYS, in the words every surface prints. Each state is a different
// fact, and the threshold state is a CONDITION rather than a winner: without the
// farm's payment yield there is no winner to name, and naming one off a default is
// the defect this module was written to end.
export const ELECTION_LABEL = {
  NEITHER: "Neither projected to pay",
  UNKNOWN: "Not available",
};
export function electionLabel(read) {
  if (!read || read.usable === false) return ELECTION_LABEL.UNKNOWN;
  switch (read.state) {
    case "neither_pays": return ELECTION_LABEL.NEITHER;
    case "arc_only": return "ARC-CO";
    case "plc_only": return "PLC";
    case "decided": return read.pick || ELECTION_LABEL.UNKNOWN;
    case "threshold": {
      // A comparison AT ONE PRICE (FSA's forecast, or the scenario price the read was made at):
      // at that price PLC's payment rises with the payment yield and ARC-CO's does not, so the
      // split is exact THERE — and only there, which is why the price is part of the label.
      const be = num(read.breakeven);
      return be == null ? ELECTION_LABEL.UNKNOWN
        : "PLC larger above " + Math.round(be) + " bu payment yield, at this price";
    }
    default: return ELECTION_LABEL.UNKNOWN;
  }
}

// ── ARC-IC ───────────────────────────────────────────────────────────────────
//
// ⚠ ARC-IC IS NOT MODELLED AND MUST NOT BE. What IS established, and what decides
// whether it is even worth investigating, is the hurdle:
//
//   7 CFR 1412 "Payment acres": for ARC-IC the payment acres are 65% of the
//   farm's covered-commodity base acres; for ARC-CO and PLC they are 85%.
//   The guarantee (90% of benchmark) and the cap (12% of benchmark) are the SAME
//   for ARC-CO and ARC-IC, so the payment factor is the entire structural
//   difference.
//
//     ARC-IC = ARC_IC_rate x 0.65 x base_acres
//     ARC-CO = ARC_CO_rate x 0.85 x base_acres     -> base acres cancel again
//
//   ARC-IC wins only if ARC_IC_rate > (0.85/0.65) x ARC_CO_rate = 1.308x.
//
// A farm must beat its own county by 31% before ARC-IC pays a dollar more.
export const ARC_IC_PAYMENT_FACTOR = 0.65;
export function arcIcHurdle() { return BASE_ACRE_FACTOR / ARC_IC_PAYMENT_FACTOR; }

// ⚠ AND NOTHING HERE DECIDES WHETHER A FARM CLEARS THAT HURDLE.
//
// A farm-vs-county screen built on RMA production history against NASS county
// yields was written, tested and then REMOVED before it could reach an agent
// (Kevin, 2026-09-17). The hurdle above is a program rule and is safe to state;
// inferring from those two series that a particular farm can or cannot beat it is
// not the same claim. ARC-IC is a different FSA determination — whole-farm revenue
// from CERTIFIED producer yields over the covered commodities actually planted,
// pooled across the producer's ARC-IC farms in the state — and RMA's insured-acre
// yields are not those yields. A divergence screen may eventually earn its place
// as a "worth investigating" signal, but it needs its own design and audit first,
// and until then this module offers no function that could be mistaken for one.

// ── WHOSE ANSWER IS IT? THE IRRIGATED PERCENTAGE BELONGS TO A FARM ───────────
//
// §1412.53(c) above says the blend is weighted by "THE FARM'S historical
// irrigated percentage", from that farm's own 2019-2023 reported acreage. So the
// answer is a property of a FARM, and two growers in one county can hold two
// different true answers.
//
// ⚠ THE FIRST VERSION KEYED IT crop|state|county AND SHIPPED (BUG-092). That key
// fixed one leak and left a worse one: it stopped an answer following the COUNTY
// picker, and it let an answer follow the FARM picker — one grower's percentage
// rating the next grower's figures, with the "not answered" notice gone and
// nothing on screen to say the number was inherited. In Van Buren MI corn that
// selects or blends between a $1,068.62 irrigated benchmark revenue and a
// $806.71 non-irrigated one, a $261.91/ac spread, on a farm nobody asked about.
//
// The key therefore carries a STABLE FARMER ID as well as the cell, and it
// FAILS CLOSED: any missing part returns null, a null key stores nothing and
// reads nothing, and an unanswered cell is UNKNOWN — which `resolvePracticeRow`
// already refuses to blend. Losing a typed answer costs one click; showing one
// farm's answer under another farm's name is the defect this exists to prevent.
//
// The id must be the farmer's own stable identifier (a uuid), never the NAME —
// this book holds two Meisenzahls and an entity whose name changed spelling
// between crop years, and a name key would merge or split them silently.
export function irrigationAnswerKey(farmerId, crop, state, county) {
  const part = (v) => {
    const s = v == null ? "" : String(v).trim();
    return s ? s.toUpperCase() : null;
  };
  const f = part(farmerId), c = part(crop), s = part(state), k = part(county);
  if (!f || !c || !s || !k) return null;
  return f + "|" + c + "|" + s + "|" + k;
}

// Read: a null key (no farm chosen, or no county yet) is UNKNOWN, never a value.
export function irrigationAnswerOf(store, key) {
  if (!key || !store) return null;
  const v = store[key];
  return v == null ? null : v;
}

// Write: a null key stores NOTHING and returns the store unchanged, so an answer
// typed with no farm identified cannot be picked up by the next farm.
export function setIrrigationAnswer(store, key, pct) {
  const s = store || {};
  if (!key) return s;
  return { ...s, [key]: pct };
}

// ── WHICH ONE DO I ENROL IN? — the election, before the year has happened ─────
//
// Kevin, 2026-09-18, reading a report that said "Neither projected to pay" for
// both counties: *"This is better; but it doesnt give us any actionable steps. I
// see neither are projected to pay today, but I still need to enroll in one,
// which one should I enroll in and why?"*
//
// He is right, and the old answer was the worst kind of true. `electionRead`
// above answers the election for a year whose price is KNOWN — it compares two
// payments. Before the year happens there are no payments to compare, so it
// returns `neither_pays` and the page had nothing to say, while the grower still
// has to sign one of them by FSA's deadline.
//
// What decides an election in advance is not a forecast. It is WHERE EACH
// PROGRAM STARTS PAYING, and those are two different questions about two
// different risks:
//
//   PLC    pays when the national season-average price (MYA) ends below the
//          effective reference price. It never looks at your county's yield.
//   ARC-CO pays when your county's REVENUE ends below its guarantee — so a
//          price fall, a county yield shortfall, or any mix of the two.
//
// Both thresholds are published figures, so how far today's forecast sits from
// each one is arithmetic, not a prediction. Measured 2026-09-18, NY wheat 2026,
// which is what made the point: USDA forecasts a $6.40 MYA against a $6.35
// reference price and a Livingston ARC-CO guarantee of $529.57/ac. PLC is FIVE
// CENTS from paying and ARC-CO is twelve. "Neither projected to pay" was hiding
// a decision that turns on a 1% move.
//
// ── THE CAP IS THE STRUCTURE, AND IT IS WHAT THE THRESHOLD IS BUILT ON ───────
//
// ARC-CO cannot pay more than 12% of its benchmark revenue however far the price
// falls; PLC is uncapped and keeps growing. So there is a price — `capPrice` —
// below which ARC-CO is maxed out and only PLC is still responding, and the
// payment yield at which PLC matches the cap THERE is the highest payment yield
// at which ARC-CO ever wins anything:
//
//     switchYield = cap / (effective reference price − capPrice)
//
// Above it, PLC pays at least as much as ARC-CO at EVERY price — on a NORMAL county crop,
// because ARC-CO's revenue here is benchmark yield × price. A short county crop raises
// ARC-CO past it (Genesee wheat 2026 at 85% of benchmark: 140 bu at $5.85 against a 77 bu
// switch), so every surface that prints it says so. That single
// number is the election, it needs no farm data to compute, and it is the thing
// to go and check a payment yield against.
//
// ⚠ IT IS NOT MONOTONIC, WHICH IS WHY THE ANSWER IS A BAND AND NOT A DIRECTION.
// Below a given payment yield ARC-CO wins over a MIDDLE range of prices and
// loses at both ends — it pays nothing before its own trigger, and it is frozen
// at the cap once the price falls far enough while PLC keeps climbing. So
// `arcWinsBetween` returns the two prices that bound that range, which is a
// statement a grower can actually check against a market.
//
// Nothing here forecasts a price, and nothing here estimates a payment yield —
// the caller supplies one or gets the threshold, per this module's own rule at
// the top of the file.
// ── A COUNTY CROP THAT IS NOT THE BENCHMARK (2026-09-19) ──
// Everything below takes ARC-CO's actual revenue as county yield × price, and until now the
// county yield was always the benchmark — "a normal county crop", which is why every surface
// that prints the switch yield says so. `yieldFactor` sets that county yield to a
// multiple of the benchmark (0.85 = a short crop) so the SAME algebra answers the county-yield
// sensitivity the farm election summary prints; no renderer re-derives it. The guarantee and
// the cap stay the BENCHMARK's (90% and 12% of benchmark revenue) — only the actual revenue
// moves. Omitted or null it is 1, and every figure is exactly what it was.
export function electionOutlook({
  effectiveRefPrice, myaPrice, benchmarkYield, benchmarkRevenue, guaranteeRevenue, paymentYield,
  yieldFactor = null, loanRate = null,
}) {
  const erp = num(effectiveRefPrice), mya = num(myaPrice);
  // FSA's national loan rate: no program price is ever below it (arcPlcRule programPrice), so a
  // crossover or cap price under it is one the market can never reach.
  const L = num(loanRate);
  const bY = num(benchmarkYield), bR = num(benchmarkRevenue), gR = num(guaranteeRevenue);
  const out = {
    plcTriggerPrice: erp, arcTriggerPrice: null, arcTriggerYield: null, arcTriggerRevenue: null,
    priceFallToPlc: null, priceFallToArc: null, yieldFallToArc: null,
    firstOnPrice: null, capPrice: null, arcCapPerBaseAcre: null,
    switchYield: null, switchReason: null, arcWinsBetween: null, lean: null, refused: null,
    capRevenue: null, benchmarkYield: null, scenarioYield: null, yieldFactor: null, loanRate: L,
  };
  if (erp == null || bY == null || bR == null || gR == null || !(bY > 0)) {
    out.refused = "the county's published benchmark is not loaded";
    return out;
  }
  const f = num(yieldFactor) != null && num(yieldFactor) > 0 ? num(yieldFactor) : 1;
  const aY = bY * f;
  out.yieldFactor = f;
  out.scenarioYield = aY;
  // Where each one starts. ARC-CO's price trigger holds the county at its
  // benchmark yield, and its yield trigger holds the price at today's forecast —
  // two slices of one revenue test, each stated with what it holds fixed.
  out.arcTriggerRevenue = gR;
  out.arcTriggerPrice = gR / aY;
  if (mya != null && mya > 0) {
    out.arcTriggerYield = gR / programPrice(mya, L);
    out.priceFallToPlc = mya - erp;
    out.priceFallToArc = mya - out.arcTriggerPrice;
    out.yieldFallToArc = bY - out.arcTriggerYield;
  }
  out.firstOnPrice = erp > out.arcTriggerPrice ? "PLC"
                   : out.arcTriggerPrice > erp ? "ARC-CO" : "tie";
  const cap = arcCapRate(bR);
  out.arcCapPerBaseAcre = cap * BASE_ACRE_FACTOR;
  out.capPrice = (gR - cap) / aY;
  out.capRevenue = cap;
  out.benchmarkYield = bY;
  // ⚠ WELL-POSED ONLY WHERE PLC IS ALREADY PAYING BY THE TIME ARC-CO MAXES OUT.
  // Where the cap is reached at a price ABOVE the reference price, ARC-CO is at
  // its ceiling before PLC has paid a cent, and no finite payment yield beats it
  // just below the reference price. Reported as a reason, never as a number.
  // ⚠ AND ONLY WHERE PLC STARTS PAYING FIRST (2026-09-18, found extending this to corn).
  // Between the cap price and ARC-CO's own trigger the crossover is (gR − bY·p)/(erp − p),
  // whose slope has the sign of (gR − bY·erp): it FALLS toward the reference price when
  // ARC-CO's trigger sits below it, so the peak is at the cap price and the closed form holds
  // — every 2026 wheat county (national $6.98 x 90% = $6.28 < $6.35). Where ARC-CO triggers
  // ABOVE the reference price the slope is positive and the crossover grows without bound as
  // the price nears it: PLC pays pennies there while ARC-CO pays real money, so NO payment
  // yield makes PLC win at every price. Genesee corn 2026: ARC-CO starts at $4.53 against a
  // $4.42 reference; the closed form said 222 bu while the sheet's own grid read 239 at $4.07.
  // Reported as a reason, never as a number.
  //
  // The cap-first case is checked FIRST because it is the narrower one: the cap price always
  // sits below ARC-CO's trigger, so a reference price at or under the cap price is also under
  // the trigger, and the less specific reason would otherwise leave this branch dead.
  if (L != null && !(erp > L))
    out.switchReason = "the national loan rate is at or above the effective reference price, so PLC cannot pay at any price";
  else if (!(erp > out.capPrice))
    out.switchReason = "ARC-CO reaches its cap before PLC pays anything, so no payment yield wins at every price";
  else if (out.arcTriggerPrice > erp)
    out.switchReason = "ARC-CO starts paying at $" + out.arcTriggerPrice.toFixed(2) + ", above the $" + erp.toFixed(2)
      + " reference price, so near that price ARC-CO pays more at any payment yield";
  // Below the loan rate the price stops falling, so where ARC-CO's cap price sits under it the
  // cap is never reached: the ratio peaks at the loan rate instead, on ARC-CO's payment THERE.
  else if (L != null && L > out.capPrice) out.switchYield = Math.max(0, gR - aY * L) / (erp - L);
  else out.switchYield = cap / (erp - out.capPrice);
  const py = num(paymentYield);
  if (py != null && py > 0 && py !== aY) {
    // Upper bound: inside the band where ARC-CO's shortfall is still growing.
    const hi = (gR - py * erp) / (aY - py);
    // Lower bound: inside the band where ARC-CO is frozen at its cap.
    const lo = erp - cap / py;
    // A bound under the loan rate is unreachable: below it the payments are frozen at their
    // loan-rate values, so the band then runs all the way down (0), as it did below a zero price.
    const Lf = L != null ? L : 0;
    const okHi = hi > Math.max(out.capPrice, Lf) && hi < erp;
    const okLo = lo < out.capPrice && lo > Lf;
    if (okHi || okLo) out.arcWinsBetween = [okLo ? lo : 0, okHi ? hi : out.capPrice];
  }
  // ⚠ THE LEAN IS THE SWITCH YIELD, AND NOTHING ELSE. Kevin, 2026-09-17: *"Payment
  // yield would be nice to have but we need to be able to make recs without it."* So
  // this names one, and it names it off the single published threshold rather than
  // off a forecast: at or above `switchYield` PLC pays at least as much at EVERY
  // price, so below it ARC-CO has prices where it pays more — and it is the only one
  // of the two that answers a county yield shortfall at all.
  //
  // The caller supplies the payment yield it has. Where that is a PLACEHOLDER the
  // caller says so, and the lean is printed with the threshold that flips it, which
  // is a recommendation carrying its own condition rather than a number we invented
  // being passed off as the answer (BUG-090). With no payment yield at all there is
  // no lean — only the threshold, which is still the useful half.
  if (out.switchYield != null && py != null && py > 0) out.lean = py >= out.switchYield ? "PLC" : "ARC-CO";
  return out;
}

// ── WHICH TO ENROL IN, FROM ONE COUNTY ROW — the election sheet and the Coverage Advisor ──
//
// Kevin, 2026-09-18, on the election sheet's "PLC beats ARC-CO above 63–148 bu/ac": "a wild
// range and weird thing to say. If it falls within that range? What does that even mean?"
// A range of crossovers across a price ladder is not a threshold anyone can hold an FSA
// record against. The one number that IS such a threshold is electionOutlook's switch
// yield — price-independent, and already what the Full Strategy Report prints (#208). This
// is that same call from the raw FSA rows the sheet and the Advisor hold, so all three
// surfaces print ONE number for one county, from one function.
export function enrolRead({ countyRow, ref, paymentYield = null, yieldFactor = null } = {}) {
  if (!countyRow || !ref) return { usable: false, switchYield: null, switchReason: null, lean: null };
  const o = electionOutlook({
    effectiveRefPrice: ref.effective_ref_price, myaPrice: ref.mya_price,
    benchmarkYield: countyRow.benchmark_yield, benchmarkRevenue: countyRow.benchmark_revenue,
    guaranteeRevenue: countyRow.guarantee_revenue, paymentYield, yieldFactor,
    loanRate: ref.national_loan_rate,
  });
  const out = { usable: o.refused == null, ...o, paymentYield: num(paymentYield) != null && num(paymentYield) > 0 ? num(paymentYield) : null };
  // ARC-first: the line for the farm's own payment yield, and a lean only where one is known —
  // PLC if USDA's forecast already sits below that line, ARC-CO otherwise. Without a payment
  // yield, ruler lines at 60 / 80 / 100% of the county benchmark (a ruler, never the farm's
  // yield) so a grower can place their own figure.
  if (out.usable && out.switchYield == null && out.switchReason) {
    out.arcFirst = true;
    out.plcBelow = plcPaysMoreBelow(o, out.paymentYield);
    const mya = num(ref.mya_price);
    if (out.plcBelow != null) out.lean = mya != null && mya < out.plcBelow ? "PLC" : "ARC-CO";
    out.lines = [0.6, 0.8, 1].map((f) => Math.round((num(o.benchmarkYield) * f) / 5) * 5)
      .map((y) => ({ paymentYield: y, below: plcPaysMoreBelow(o, y) })).filter((l) => l.below != null);
  }
  return out;
}
// ── WHERE ARC-CO TRIGGERS FIRST, THE PAYMENT YIELD SETS A PRICE LINE (2026-09-18) ─────
// Kevin, on the corn sheet printing "No single payment yield decides this": "why is corn not
// making a rec". The reason stands — where ARC-CO starts paying ABOVE the reference price
// (Genesee corn 2026: $4.53 against $4.42) no payment yield makes PLC win at every price —
// but it is a reason, not a recommendation. The recommendation is structural and it is
// arithmetic: as prices fall ARC-CO pays first, and it is the only one of the two that pays on
// a short county crop, so it pays at least as much at every price down to ONE line, below
// which PLC pays more. That line depends only on the payment yield Y (normal county crop):
//   ARC-CO capped (price <= cap price):   reference − cap ÷ Y
//   ARC-CO not yet capped:                 (guarantee − Y·reference) ÷ (benchmark − Y)
// Checked by hand, Genesee corn 2026, Y = 250: $4.128, and the crossover there is 250 bu.
export function plcPaysMoreBelow(e, paymentYield) {
  const Y = num(paymentYield);
  if (!e || e.switchYield != null || !e.switchReason || !(Y > 0)) return null;
  // The COUNTY yield the outlook was built on (the benchmark unless a county-yield factor was
  // given) — the crossover is in ARC-CO's actual revenue, which is county yield × price.
  const erp = num(e.plcTriggerPrice), cap = num(e.capRevenue), gR = num(e.arcTriggerRevenue),
        bY = num(e.scenarioYield != null ? e.scenarioYield : e.benchmarkYield), capP = num(e.capPrice);
  if ([erp, cap, gR, bY, capP].some((v) => v == null)) return null;
  // There is ONE crossover (ARC-CO leads just under the reference price and PLC overtakes once);
  // below the national loan rate the price stops falling, so a crossover under it is never
  // reached and PLC is never the larger payment.
  const L = num(e.loanRate);
  const reach = (p) => (L == null || p > L ? p : null);
  const capped = erp - cap / Y;
  if (capped <= capP) return capped > 0 ? reach(capped) : null;
  if (bY === Y) return null;
  const p = (gR - Y * erp) / (bY - Y);
  return p > capP && p < erp ? reach(p) : null;
}
// The recommendation in words. Without the farm's payment yield it is the CONDITION, and it
// names both sides — the side IS the payment yield, so choosing one for the grower would be
// the 60 bu default by another route. With the farm's own yield it names the program.
export function enrolLabel(e) {
  if (!e || !e.usable) return null;
  const sw = num(e.switchYield);
  if (sw == null && e.arcFirst) {
    const d = (v) => "$" + Number(v).toFixed(2);
    if (e.lean === "PLC") return "Enroll in PLC — at your " + Math.round(e.paymentYield) + " bu payment yield PLC pays more below " + d(e.plcBelow) + ", and USDA's forecast is below that";
    if (e.lean === "ARC-CO") return "Enroll in ARC-CO — at your " + Math.round(e.paymentYield) + " bu payment yield, PLC pays more only if the price falls below " + d(e.plcBelow);
    const top = (e.lines || [])[(e.lines || []).length - 1];
    return "Enroll in ARC-CO unless the price falls below your payment yield's line" + (top ? " (" + d(top.below) + " at " + top.paymentYield + " bu)" : "");
  }
  if (sw == null) return e.switchReason ? "No single payment yield decides this: " + e.switchReason : null;
  const n = Math.round(sw);
  if (e.lean === "PLC") return "Enroll in PLC — the " + Math.round(e.paymentYield) + " bu payment yield is above the " + n + " bu switch";
  // ⚠ BELOW THE SWITCH IS NOT "ARC-CO" (Kevin, 2026-09-19). Below it ARC-CO's modeled payment
  // is larger only over PART of the price range — PLC still pays first between ARC-CO's trigger
  // and the reference price — so the side is stated with its band, never as a winner.
  if (e.lean === "ARC-CO") return "At the " + Math.round(e.paymentYield) + " bu payment yield (below the " + n + " bu switch), "
    + belowSwitchArc(e.arcWinsBetween);
  return "Enroll in PLC if the FSA payment yield is above " + n + " bu; below it, " + BELOW_SWITCH_ARC;
}

// ── THE BELOW-THE-SWITCH STATEMENT — one wording, every surface (Kevin, 2026-09-19) ──
// The farm one-pager, the Coverage Advisor, the election sheet and the Full Strategy Report
// all print this, so none of them can collapse the conditional result into "below it, ARC-CO".
export const BELOW_SWITCH_ARC = "ARC-CO's modeled payment is larger over part of the MYA range";
// With the farm's own payment yield the part is known: the band of prices where ARC-CO leads.
export function belowSwitchArc(band) {
  const b = Array.isArray(band) && band.length === 2 && band.every((v) => num(v) != null) ? band : null;
  if (!b) return BELOW_SWITCH_ARC;
  const d = (v) => "$" + Number(v).toFixed(2);
  return num(b[0]) > 0
    ? "ARC-CO's modeled payment is larger only if the MYA ends between " + d(b[0]) + " and " + d(b[1])
    : "ARC-CO's modeled payment is larger only if the MYA ends below " + d(b[1]);
}
